import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Button,
  CombinedNomenclatureInput,
  ErrorDisplay,
  FormComboBox,
  FormInput,
  FormNumericInput,
  HeadlessTable,
  Loader,
  PageTitle,
} from "@/shared/components";
import { useBreadcrumb, useToast } from "@/shared/hooks";
import { formatReportingPeriod } from "@/utils/dateUtils";
import {
  useCombinedNomenclatureUnitOfMeasure,
  useCountries,
  useDeliveryTerms,
  useModeOfTransports,
  useNatureOfTransactions,
  useRegions,
  useUnitOfMeasures,
  validateCombinedNomenclatureCode,
} from "@/hooks/useClassifierQueries";
import {
  useCreateReportItem,
  useReportItem,
  useReportItems,
  useUpdateReportItem,
} from "@/hooks/useReportQueries";
import { handleApiError } from "@/hooks/useApiQueries";
import { UiReportItem, UiValidationError } from "@/generated/ui-api";
import { useAuth } from "@/auth";
import { useVersionColumns } from "@/hooks/useVersionColumns";
import { useCheckVatValid } from "@/hooks/useBusinessEntityQueries";

const createReportItemSchema = (validationDate: string, isExport: boolean) =>
  z.object({
    combinedNomenclatureCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired")
      .refine(async code => {
        return await validateCombinedNomenclatureCode(code, validationDate);
      }, "error.invalidNomenclatureCode"),
    natureOfTransactionCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired"),
    deliveryTermsCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired"),
    modeOfTransportCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired"),
    countryOfDispatchOrDestinationCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired"),
    countryOfOriginCode: z
      .string("error.fieldRequired")
      .min(1, "error.fieldRequired"),
    netMass: z.number("error.fieldRequired").min(0, "error.fieldPositive"),
    quantityInSupplementaryUnitOfMeasure: z.number().nullish(),
    unitOfMeasureCode: z.string().nullish(),
    invoicedAmount: z
      .number("error.fieldRequired")
      .min(0, "error.fieldPositive"),
    statisticalValue: z.number().min(0, "error.fieldPositive").nullish(),
    partnerIdentifier: isExport
      ? z
          .string("error.partnerCodeMissing")
          .trim()
          .min(1, "error.partnerCodeMissing")
      : z.string().trim().nullish(),
    regionCode: z.string().nullish(),
  });

type ReportItemFormData = z.infer<ReturnType<typeof createReportItemSchema>>;

export default function ReportItemFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { versionId, itemId } = useParams<{
    versionId: string;
    itemId?: string;
  }>();
  const versionIdNum = Number(versionId);
  const itemIdNum =
    itemId && itemId !== "new" && !isNaN(Number(itemId))
      ? Number(itemId)
      : undefined;
  const { representedEntity } = useAuth();

  const { setBreadcrumbs } = useBreadcrumb();

  const {
    data: reportItemsData,
    isLoading: isReportItemsLoading,
    error: error,
  } = useReportItems(versionIdNum, 1, 1, {
    enabled: !!representedEntity,
  });

  const tradeDirection = reportItemsData?.reportHeader.tradeDirection;
  const isExport = tradeDirection === "EXPORT";

  const { versionColumns, getTradeDirectionLabel } =
    useVersionColumns(tradeDirection);

  const businessEntityName =
    reportItemsData?.reportHeader?.vatPayerInfo?.vatPayerTitle;
  const businessEntityVat =
    reportItemsData?.reportHeader?.vatPayerInfo?.vatPayerIdentifierVAT;
  const version = reportItemsData?.version;

  const versionData = useMemo(
    () =>
      version && reportItemsData
        ? [
            {
              ...version,
              reportingPeriod: formatReportingPeriod(
                reportItemsData.reportHeader.reportingPeriod.reportingYear,
                reportItemsData.reportHeader.reportingPeriod.reportingMonth
              ),
              tradeDirection: reportItemsData.reportHeader.tradeDirection,
            },
          ]
        : [],
    [version, reportItemsData]
  );

  const versionTable = useReactTable({
    data: versionData,
    columns: versionColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const reportingPeriod = reportItemsData?.reportHeader.reportingPeriod;
  const validationDate = reportingPeriod
    ? new Date(reportingPeriod.reportingYear, reportingPeriod.reportingMonth, 0)
        .toISOString()
        .split("T")[0]
    : new Date().toISOString().split("T")[0];

  const reportItemSchema = useMemo(
    () => createReportItemSchema(validationDate, isExport),
    [validationDate, isExport]
  );

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
    setValue,
  } = useForm<ReportItemFormData>({
    resolver: zodResolver(reportItemSchema),
    mode: "onBlur",
    defaultValues: {
      combinedNomenclatureCode: "",
      natureOfTransactionCode: "",
      deliveryTermsCode: "",
      modeOfTransportCode: "",
      countryOfDispatchOrDestinationCode: "",
      countryOfOriginCode: "",
      netMass: 0,
      unitOfMeasureCode: "",
      invoicedAmount: 0,
      statisticalValue: null,
      partnerIdentifier: "",
      regionCode: "",
    },
  });

  const { data: reportItem, isLoading: isReportItemLoading } = useReportItem(
    versionIdNum,
    itemIdNum,
    !!representedEntity
  );

  const partnerIdentifierWatch = useWatch({
    control,
    name: "partnerIdentifier",
  });

  const isVatCheckDisabled = partnerIdentifierWatch === "QV999999999999";

  const { refetch: checkVatValid } = useCheckVatValid(
    partnerIdentifierWatch || "",
    false
  );

  const handleCheckIdais = async () => {
    if (!partnerIdentifierWatch) return;
    try {
      const { data: isValid } = await checkVatValid();
      if (isValid) {
        showToast(t("reportItemForm.notifications.vatValid"), "success");
      } else {
        showToast(t("reportItemForm.notifications.vatInvalid"), "error");
      }
    } catch (error) {
      showToast(handleApiError(error as Error, t), "error");
    }
  };

  const handleCheckVies = () => {
    window.open(
      "https://ec.europa.eu/taxation_customs/vies/#/vat-validation",
      "_blank"
    );
  };

  const { mutate: createReportItem, isPending: isCreating } =
    useCreateReportItem(versionIdNum);

  const { mutate: updateReportItem, isPending: isUpdating } =
    useUpdateReportItem(versionIdNum, itemIdNum!);

  const isSaving = isCreating || isUpdating;

  const isEditable = itemIdNum ? (reportItem?.isEditable ?? false) : true;

  const combinedNomenclatureCodeWatch = useWatch({
    control,
    name: "combinedNomenclatureCode",
  });

  const unitOfMeasureCodeWatch = useWatch({
    control,
    name: "unitOfMeasureCode",
  });

  const regionCodeWatch = useWatch({
    control,
    name: "regionCode",
  });

  const quantityWatch = useWatch({
    control,
    name: "quantityInSupplementaryUnitOfMeasure",
  });

  const { data: unitOfMeasureCode } = useCombinedNomenclatureUnitOfMeasure(
    combinedNomenclatureCodeWatch
  );

  useEffect(() => {
    if (unitOfMeasureCode) {
      setValue("unitOfMeasureCode", unitOfMeasureCode, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [unitOfMeasureCode, setValue]);

  const shouldShowUnitOfMeasure =
    !!unitOfMeasureCodeWatch ||
    !!unitOfMeasureCode ||
    !!quantityWatch ||
    !!reportItem?.quantityInSupplementaryUnitOfMeasure ||
    !!errors.unitOfMeasureCode ||
    !!errors.quantityInSupplementaryUnitOfMeasure;

  const fieldNames = useMemo(
    () => Object.keys(reportItemSchema.shape),
    [reportItemSchema]
  );
  const handleValidationErrors = useCallback(
    (validationErrors: UiValidationError[]) => {
      validationErrors.forEach(validationError => {
        let isFieldError = false;
        if (validationError.validationRuleDataFields) {
          validationError.validationRuleDataFields.forEach(field => {
            const fieldName =
              field.dataFieldName === "supplementaryUnitOfMeasureCode"
                ? "unitOfMeasureCode"
                : field.dataFieldName;

            if (fieldName && fieldNames.includes(fieldName)) {
              setError(fieldName as keyof ReportItemFormData, {
                type: "manual",
                message: validationError.messageText,
              });
              isFieldError = true;
            }
          });
        }

        if (!isFieldError) {
          showToast(
            validationError.messageText || t("error.errorOccurred"),
            "error"
          );
        }
      });
    },
    [fieldNames, setError, showToast, t]
  );

  useEffect(() => {
    if (reportItem) {
      reset(reportItem as unknown as ReportItemFormData);
      if (reportItem.validationErrors) {
        handleValidationErrors(reportItem.validationErrors);
      }
    }
  }, [reportItem, reset, handleValidationErrors]);

  const { data: regions, isLoading: isRegionsLoading } =
    useRegions(validationDate);
  const {
    data: natureOfTransactions,
    isLoading: isNatureOfTransactionsLoading,
  } = useNatureOfTransactions(validationDate);
  const { data: deliveryTerms, isLoading: isDeliveryTermsLoading } =
    useDeliveryTerms(validationDate);
  const { data: modeOfTransports, isLoading: isModeOfTransportsLoading } =
    useModeOfTransports(validationDate);
  const { data: unitOfMeasures, isLoading: isUnitOfMeasuresLoading } =
    useUnitOfMeasures(validationDate);
  const { data: countries, isLoading: isCountriesLoading } =
    useCountries(validationDate);

  const filteredCountries = useMemo(() => {
    if (!countries) return [];
    return countries.filter((c: { isEuMember?: boolean }) => c.isEuMember);
  }, [countries]);

  useEffect(() => {
    if (reportItemsData) {
      const breadcrumbs = [
        { label: t("breadcrumbs.reports"), path: "/reports/summaries" },
        {
          label: `${getTradeDirectionLabel(
            reportItemsData.reportHeader.tradeDirection
          )} ${formatReportingPeriod(
            reportItemsData.reportHeader.reportingPeriod.reportingYear,
            reportItemsData.reportHeader.reportingPeriod.reportingMonth
          )}`,
          path: `/report/${reportItemsData.reportHeader.reportId}`,
        },
        {
          label: `${t("breadcrumbs.itemList")} ${
            reportItemsData.version?.versionNumber ?? ""
          }`,
          path: `/reports/versions/${versionIdNum}/items`,
        },
        {
          label: itemId ? t("breadcrumbs.editItem") : t("breadcrumbs.newItem"),
        },
      ];
      setBreadcrumbs(breadcrumbs);
    }
  }, [
    t,
    versionId,
    setBreadcrumbs,
    reportItemsData,
    itemId,
    versionIdNum,
    tradeDirection,
    getTradeDirectionLabel,
  ]);

  const onSubmit = (data: ReportItemFormData) => {
    const onSettled = {
      onSuccess: () => {
        showToast(t("reportItemForm.notifications.saved"), "success");
        navigate(`/reports/versions/${versionIdNum}/items`);
      },
      onError: (error: unknown) => {
        const errObj = error as { body?: unknown };
        const validationErrors = errObj?.body || error;
        if (
          Array.isArray(validationErrors) &&
          validationErrors.length > 0 &&
          "validationRuleDataFields" in validationErrors[0]
        ) {
          handleValidationErrors(validationErrors as UiValidationError[]);
        } else {
          showToast(handleApiError(error as Error, t), "error");
        }
      },
    };

    const payload: UiReportItem = {
      ...reportItem,
      ...data,
      partnerIdentifier: data.partnerIdentifier || undefined,
      regionCode: data.regionCode || undefined,
      unitOfMeasureCode: data.unitOfMeasureCode || undefined,
      quantityInSupplementaryUnitOfMeasure:
        data.quantityInSupplementaryUnitOfMeasure ?? undefined,
      statisticalValue: data.statisticalValue ?? undefined,
    };

    if (itemIdNum) {
      updateReportItem(payload, onSettled);
    } else {
      createReportItem(payload, onSettled);
    }
  };

  if (!representedEntity) {
    return <Loader />;
  }

  if (isReportItemsLoading || isReportItemLoading) return <Loader />;
  if (!reportItemsData)
    return <ErrorDisplay message={handleApiError(error, t)} />;

  const shouldShowPartnerId =
    isExport ||
    !!reportItem?.partnerIdentifier ||
    !!partnerIdentifierWatch ||
    !!errors.partnerIdentifier;

  const shouldShowRegion =
    isExport ||
    !!reportItem?.regionCode ||
    !!regionCodeWatch ||
    !!errors.regionCode;

  return (
    <div className="max-w-6xl mx-auto">
      <PageTitle>
        {reportItem?.reportItemNumber &&
          `${t("reportItemForm.reportItemNumber")} ${reportItem.reportItemNumber}`}
      </PageTitle>

      <div className="mt-4 mb-8">
        {isExport && (
          <div className="space-y-1 text-sm text-black mb-6">
            <div className="whitespace-nowrap">
              <span className="font-bold">
                {t("reportPage.reportInfo.vatPayerTitle")}:
              </span>{" "}
              {businessEntityName}
            </div>
            <div className="whitespace-nowrap">
              <span className="font-bold">
                {t("reportPage.reportInfo.vatPayerCode")}:
              </span>{" "}
              {businessEntityVat}
            </div>
          </div>
        )}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <HeadlessTable table={versionTable} />
        </div>
      </div>

      {isExport && (
        <PageTitle size="md" className="mb-6">
          {t("reportItemForm.formTitle")}
        </PageTitle>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <Controller
            name="combinedNomenclatureCode"
            control={control}
            render={({ field }) => (
              <CombinedNomenclatureInput
                label={t("reportItemForm.fields.productCode")}
                value={field.value}
                onChange={code => field.onChange(code || "")}
                onBlur={field.onBlur}
                error={errors.combinedNomenclatureCode?.message}
                required
                validationDate={validationDate}
                readOnly={!isEditable}
              />
            )}
          />

          {shouldShowPartnerId && (
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <Controller
                  name="partnerIdentifier"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      label={t("reportItemForm.fields.partnerId")}
                      {...field}
                      value={field.value ?? ""}
                      onChange={value => field.onChange(value.trim())}
                      onBlur={field.onBlur}
                      error={errors.partnerIdentifier?.message}
                      required={isExport}
                      readOnly={!isEditable}
                    />
                  )}
                />
              </div>
              {isExport && (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="mb-px"
                    onClick={handleCheckIdais}
                    type="button"
                    disabled={isVatCheckDisabled}
                  >
                    {t("reportItemForm.checkIdais")}
                  </Button>
                  <Button
                    variant="secondary"
                    className="mb-px"
                    onClick={handleCheckVies}
                    type="button"
                    disabled={isVatCheckDisabled}
                  >
                    {t("reportItemForm.checkVies")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            {shouldShowRegion && (
              <Controller
                name="regionCode"
                control={control}
                render={({ field }) => (
                  <FormComboBox
                    label={t("reportItemForm.fields.county")}
                    items={regions || []}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.regionCode?.message}
                    isLoading={isRegionsLoading}
                    readOnly={!isEditable}
                  />
                )}
              />
            )}

            <Controller
              name="natureOfTransactionCode"
              control={control}
              render={({ field }) => (
                <FormComboBox
                  label={t("reportItemForm.fields.transaction")}
                  items={natureOfTransactions || []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.natureOfTransactionCode?.message}
                  required
                  isLoading={isNatureOfTransactionsLoading}
                  readOnly={!isEditable}
                />
              )}
            />

            <Controller
              name="deliveryTermsCode"
              control={control}
              render={({ field }) => (
                <FormComboBox
                  label={t("reportItemForm.fields.deliveryConditions")}
                  items={deliveryTerms || []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.deliveryTermsCode?.message}
                  required
                  isLoading={isDeliveryTermsLoading}
                  readOnly={!isEditable}
                />
              )}
            />

            <Controller
              name="modeOfTransportCode"
              control={control}
              render={({ field }) => (
                <FormComboBox
                  label={t("reportItemForm.fields.transportType")}
                  items={modeOfTransports || []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.modeOfTransportCode?.message}
                  required
                  isLoading={isModeOfTransportsLoading}
                  readOnly={!isEditable}
                />
              )}
            />
          </div>

          <div className="space-y-4">
            <Controller
              name="countryOfDispatchOrDestinationCode"
              control={control}
              render={({ field }) => (
                <FormComboBox
                  label={
                    isExport
                      ? t("reportItemForm.fields.destinationCountry")
                      : t("reportItemForm.fields.dispatchCountry")
                  }
                  items={filteredCountries}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.countryOfDispatchOrDestinationCode?.message}
                  required
                  isLoading={isCountriesLoading}
                  readOnly={!isEditable}
                />
              )}
            />

            <Controller
              name="countryOfOriginCode"
              control={control}
              render={({ field }) => (
                <FormComboBox
                  label={t("reportItemForm.fields.countryOfOrigin")}
                  items={countries || []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.countryOfOriginCode?.message}
                  required
                  isLoading={isCountriesLoading}
                  readOnly={!isEditable}
                />
              )}
            />
          </div>

          <div className="space-y-4">
            <Controller
              name="netMass"
              control={control}
              render={({ field }) => (
                <FormNumericInput
                  label={t("reportItemForm.fields.netWeight")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.netMass?.message}
                  required
                  readOnly={!isEditable}
                  decimalScale={3}
                />
              )}
            />

            {shouldShowUnitOfMeasure && (
              <>
                <Controller
                  name="unitOfMeasureCode"
                  control={control}
                  render={({ field }) => (
                    <FormComboBox
                      label={t("reportItemForm.fields.unitCode")}
                      items={unitOfMeasures || []}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.unitOfMeasureCode?.message}
                      disabled={!!unitOfMeasureCode}
                      isLoading={isUnitOfMeasuresLoading}
                      readOnly={!isEditable}
                    />
                  )}
                />

                <Controller
                  name="quantityInSupplementaryUnitOfMeasure"
                  control={control}
                  render={({ field }) => (
                    <FormNumericInput
                      label={t("reportItemForm.fields.quantity")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={
                        errors.quantityInSupplementaryUnitOfMeasure?.message
                      }
                      readOnly={!isEditable}
                      decimalScale={3}
                    />
                  )}
                />
              </>
            )}

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Controller
                  name="invoicedAmount"
                  control={control}
                  render={({ field }) => (
                    <FormNumericInput
                      label={t("reportItemForm.fields.invoiceValue")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.invoicedAmount?.message}
                      required
                      readOnly={!isEditable}
                    />
                  )}
                />
              </div>
            </div>

            <Controller
              name="statisticalValue"
              control={control}
              render={({ field }) => (
                <FormNumericInput
                  label={t("reportItemForm.fields.statisticalValue")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.statisticalValue?.message}
                  readOnly={!isEditable}
                />
              )}
            />
          </div>
        </div>

        <div className="flex justify-center pt-6">
          {isEditable ? (
            <Button
              variant="primary"
              size="lg"
              disabled={isSaving}
              onClick={handleSubmit(onSubmit, () => {
                showToast(t("error.validation"), "error");
              })}
              className="px-8 py-3"
            >
              {t("reportItemForm.save")}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="lg"
              onClick={() =>
                navigate(`/reports/versions/${versionIdNum}/items`)
              }
              className="px-8 py-3"
            >
              {t("common.close")}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
